import { useEffect, useRef } from "react";
import { useRealtimeOptional } from "../../context/RealtimeContext";
import type { RealtimeEvent } from "../../services/realtimeService";

/**
 * Live-Rückmeldung zur eigenen Freigabe-Anfrage (01.10.2026).
 *
 * Genehmigt oder lehnt jemand die Anfrage ab, bekommt der Antragsteller eine
 * Mitteilung — und deren Realtime-Frame (Kanal `notification`, Typ
 * `approval_decision`, Bezug KONTAKT/refId) ist das Signal für die offene
 * Akte: Sie lädt `canWrite` und den Stand der Anfrage neu, ohne dass die
 * Seite neu geladen werden muss. Nach einem SSE-Abriss (`reconnect`) lädt sie
 * ebenfalls neu, weil der Frame verloren sein kann.
 */

export type FreigabeEntscheidung = {
    /** Titel der Mitteilung („Freigabe erteilt: …" / „… abgelehnt: …"). */
    titel: string;
    /** Wer entschieden hat (Anzeigename), sofern mitgeliefert. */
    von: string | null;
    /** Zeitpunkt der Mitteilung (ISO) — praktisch der Entscheidungszeitpunkt. */
    zeit: string | null;
    art: "genehmigt" | "abgelehnt" | "entzogen" | "unbekannt";
};

/** Liest aus einem Realtime-Frame die Entscheidung zu GENAU diesem Kontakt — sonst `null`. */
export function freigabeEntscheidungAusFrame(
    evt: Pick<RealtimeEvent, "channel" | "type" | "payload"> | null | undefined,
    contactId: number,
): FreigabeEntscheidung | null {
    if (!evt || evt.channel !== "notification" || evt.type !== "approval_decision") return null;
    const p = (evt.payload ?? {}) as {
        refType?: string | null;
        refId?: number | string | null;
        title?: string | null;
        actorName?: string | null;
        createdAt?: string | null;
    };
    if (p.refType !== "KONTAKT" || Number(p.refId) !== Number(contactId)) return null;
    const titel = String(p.title ?? "");
    const art: FreigabeEntscheidung["art"] = /erteilt/i.test(titel)
        ? "genehmigt"
        : /abgelehnt/i.test(titel)
          ? "abgelehnt"
          : /entzogen/i.test(titel)
            ? "entzogen"
            : "unbekannt";
    return { titel, von: p.actorName ?? null, zeit: p.createdAt ?? null, art };
}

/**
 * Ruft `onEntscheidung` auf, sobald über eine Anfrage zu `contactId`
 * entschieden wurde (bzw. nach einem SSE-Wiederaufbau mit `null`).
 * `aktiv = false` (z. B. Nutzer darf ohnehin schreiben) meldet nichts an.
 */
export function useFreigabeEntscheidungLive(
    contactId: number | null | undefined,
    aktiv: boolean,
    onEntscheidung: (e: FreigabeEntscheidung | null) => void,
): void {
    const rt = useRealtimeOptional();
    const cbRef = useRef(onEntscheidung);
    cbRef.current = onEntscheidung;
    const subscribe = rt?.subscribe;
    useEffect(() => {
        if (!subscribe || !aktiv || !contactId) return;
        const ab1 = subscribe("notification", (evt) => {
            const e = freigabeEntscheidungAusFrame(evt, contactId);
            if (e) cbRef.current(e);
        });
        const ab2 = subscribe("reconnect", () => cbRef.current(null));
        return () => {
            ab1();
            ab2();
        };
    }, [subscribe, aktiv, contactId]);
}
